// Decides which quality banner the test review screen shows, and its wording.
//
// Pure: state in, a description out. No component, no store, no request, so the
// five branches can be read in one screen and checked without running the app.
//
// Wording was agreed with the PO and the QC lead. A banner says what was found
// and nothing more; it never tells a reviewer what to do about it. Apply that to
// any check added later.

// A finding's stage, in the words a reviewer reads.
const CATEGORY_LABELS = {
  video: "The video",
  analysis: "The analysis",
  sample: "The sample",
};

// Spelled out while the check set is small, digits beyond it.
const COUNT_WORDS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven"];

function countWord(n) {
  return COUNT_WORDS[n] || String(n);
}

// Which banner to render, or null for none.
//
//   assessment    `ml_reliability` from the detail response. Null means no
//                 assessment exists, which is not the same as a clean one.
//   mlDispatched  Whether an analysis was ever asked for. False means no counts
//                 will arrive however long anyone waits.
//   reviewedAt    Set once a person has saved the results.
//   hasCounts     Whether any per-pest result rows exist.
export function resolveBanner({ assessment, mlDispatched, reviewedAt, hasCounts }) {
  // An assessment outranks everything, including a test already saved. A
  // finding is a permanent fact about the test and must not disappear from the
  // screen because the reviewer acted on it.
  if (assessment && assessment.verdict === "acceptable") {
    return {
      variant: "acceptable",
      icon: "☑️",
      title: "Video Quality Check: Passed",
      message:
        "Video Sentinel quality checks passed. The video meets all quality standards.",
      findings: null,
    };
  }

  // Anything other than acceptable needs a person, so an unrecognised verdict
  // lands here rather than claiming nothing was assessed. Only passed and
  // not-applicable checks let an assessment come back clean, and a verdict we
  // cannot read is not evidence that they did.
  if (assessment) {
    const findings = Array.isArray(assessment.findings) ? assessment.findings : [];

    // A finding verdict with nothing to list is reachable, and it is worth
    // knowing how. Two rules decide which checks count against a verdict, and
    // they are written in opposite senses: the pipeline treats any state that
    // is not `passed` and not `not_applicable` as counting, while the API lists
    // only `failed` and `not_run`. Those agree across the four states defined
    // today and stop agreeing the moment a fifth is added, at which point the
    // verdict says `finding` and the list arrives empty.
    //
    // So fall back to the pipeline's own sentence rather than announcing nought
    // findings, and let the reviewer act on the verdict itself.
    if (!findings.length) {
      return {
        variant: "reject",
        icon: "🚫",
        title: "Review Needed",
        message:
          assessment.summary ||
          "Video Sentinel did not pass this test, without saying which check.",
        findings: null,
      };
    }

    // Checks that failed are the story; checks that could not run are context.
    // When the stitched image cannot be built there is nothing to measure
    // brightness, focus or coverage on, so all three report not_run and one
    // problem reads as four. Gathering them into a line keeps the banner from
    // rivalling the counts table it sits under, and names every one of them.
    //
    // Split on state, never on a code: the dashboard renders label, state and
    // statement and nothing else, which is what lets a check be added upstream
    // with no change here.
    const failed = findings.filter((f) => f.state === "failed");
    const couldNotRun = findings.filter((f) => f.state !== "failed");

    // Only worth gathering when something failed alongside. A test whose sole
    // finding is a scorer that would not load has one thing to say, and
    // collapsing it would say nothing.
    const gather = failed.length > 0 && couldNotRun.length > 1;
    const listed = gather ? failed : findings;

    const noun = findings.length === 1 ? "flagged check" : "flagged checks";
    return {
      variant: "reject",
      icon: "🚫",
      title: `${countWord(findings.length)} ${noun} on this test - Review Needed`,
      message: null,
      findings: listed.map((f) => ({
        category: CATEGORY_LABELS[f.category] || "",
        label: f.label || f.code,
        statement: f.statement,
      })),
      // Reads as a subset of the title's count, so the two add up.
      couldNotRun: gather
        ? `${countWord(couldNotRun.length)} of these could not be checked at ` +
          `all: ${couldNotRun.map((f) => f.label || f.code).join(", ")}.`
        : null,
    };
  }

  // Nothing is known from here down. Saying so on a test somebody has already
  // finished raises an alarm about a question that is closed, so say nothing.
  if (reviewedAt) {
    return null;
  }

  if (!mlDispatched) {
    return {
      variant: "pending",
      icon: "⛔",
      title: "No analysis was run",
      message: "This test was never analysed and no counts will arrive.",
      findings: null,
    };
  }

  // Counts with no reviewer behind them can only have come from the pipeline,
  // so their presence separates an analysis that finished without an assessment
  // from one that has not finished.
  if (hasCounts) {
    return {
      variant: "warning",
      icon: "⚠️",
      title: "No quality check on this video",
      message:
        "Video Sentinel did not assess this video, so nothing is known about " +
        "its quality or its effect on the counts.",
      findings: null,
    };
  }

  // Named for the pipeline that does the counting. Video Sentinel is the
  // quality layer and produces no counts, so a message about missing counts
  // must not be attributed to it.
  return {
    variant: "pending",
    icon: "⏳",
    title: "Analysis still running",
    message: "ML Analysis has not returned counts for this test yet.",
    findings: null,
  };
}

// Whether the reviewer is offered the ML counts. Only on a clean verdict:
// a single finding is enough to withhold it, and so is a test never checked.
export function shouldOfferMlCopy(assessment) {
  return Boolean(assessment && assessment.verdict === "acceptable");
}
